import type { Action, ActionType, ItemKind, Vec2 } from "@cc/protocol";

export interface ParseOutcome {
  readonly ok: boolean;
  readonly action?: Action;
  readonly error?: string;
  /** Raw text, kept for debugging and for the "model said nonsense" replay. */
  readonly raw: string;
}

const VALID_ACTIONS: ReadonlySet<string> = new Set<ActionType>([
  "MOVE",
  "SCOUT",
  "COLLECT",
  "ATTACK",
  "DEFEND",
  "WAIT",
  "FLEE",
  "EXCHANGE",
  "USE",
]);

/** Maps common Chinese / alias verbs onto the protocol action names. */
const ACTION_ALIASES: Readonly<Record<string, ActionType>> = {
  MOVE: "MOVE",
  WALK: "MOVE",
  GO: "MOVE",
  移动: "MOVE",
  走: "MOVE",
  SCOUT: "SCOUT",
  SCAN: "SCOUT",
  侦察: "SCOUT",
  探查: "SCOUT",
  COLLECT: "COLLECT",
  PICK: "COLLECT",
  GATHER: "COLLECT",
  收集: "COLLECT",
  拾取: "COLLECT",
  ATTACK: "ATTACK",
  HIT: "ATTACK",
  攻击: "ATTACK",
  DEFEND: "DEFEND",
  BLOCK: "DEFEND",
  防御: "DEFEND",
  防守: "DEFEND",
  WAIT: "WAIT",
  IDLE: "WAIT",
  等待: "WAIT",
  FLEE: "FLEE",
  RETREAT: "FLEE",
  RUN: "FLEE",
  逃跑: "FLEE",
  撤退: "FLEE",
  EXCHANGE: "EXCHANGE",
  BUY: "EXCHANGE",
  兑换: "EXCHANGE",
  USE: "USE",
  ACTIVATE: "USE",
  使用: "USE",
};

/**
 * Extracts the first balanced JSON object from arbitrary model output.
 *
 * Models wrap JSON in fences, prepend "Sure! Here's my move:", or emit trailing
 * commentary. We would rather salvage a valid action than throw away a decision,
 * because a dropped decision means a cricket freezes for a full second on screen.
 */
export function extractJsonObject(text: string): string | null {
  const trimmed = text.trim();

  // Fast path: whole thing is JSON.
  if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
    const candidate = trimmed;
    if (isBalanced(candidate)) return candidate;
  }

  const fenced = /```(?:json)?\s*([\s\S]*?)```/i.exec(trimmed);
  if (fenced?.[1]) {
    const inner = fenced[1].trim();
    if (isBalanced(inner)) return inner;
  }

  // Strip thinking / reasoning wrappers some gateways leave in `content`.
  const withoutThink = trimmed
    .replace(/<think>[\s\S]*?<\/think>/gi, " ")
    .replace(/<reasoning>[\s\S]*?<\/reasoning>/gi, " ")
    .trim();
  const searchIn = withoutThink !== trimmed ? withoutThink : trimmed;

  const start = searchIn.indexOf("{");
  if (start === -1) return null;
  let depth = 0;
  let inString = false;
  let escaped = false;
  for (let i = start; i < searchIn.length; i++) {
    const ch = searchIn[i]!;
    if (inString) {
      if (escaped) escaped = false;
      else if (ch === "\\") escaped = true;
      else if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') inString = true;
    else if (ch === "{") depth++;
    else if (ch === "}") {
      depth--;
      if (depth === 0) return searchIn.slice(start, i + 1);
    }
  }
  return null;
}

function isBalanced(s: string): boolean {
  let depth = 0;
  let inString = false;
  let escaped = false;
  for (const ch of s) {
    if (inString) {
      if (escaped) escaped = false;
      else if (ch === "\\") escaped = true;
      else if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') inString = true;
    else if (ch === "{") depth++;
    else if (ch === "}") depth--;
    if (depth < 0) return false;
  }
  return depth === 0;
}

/**
 * Repairs the most common "almost JSON" shapes LLMs emit.
 *
 * Strict `JSON.parse` rejection of trailing commas / single quotes / bare keys
 * is correct for a wire protocol, but wrong as the *only* gate between a paid
 * model call and a `[fallback] unparseable response` timeline entry. These
 * repairs are intentionally narrow: they never invent an action the model did
 * not name.
 */
export function repairJson(text: string): string {
  let s = text.trim();
  // Trailing commas before } or ].
  s = s.replace(/,\s*([\]}])/g, "$1");
  // Bare keys: {action: "MOVE"} → {"action": "MOVE"}
  s = s.replace(/([,{]\s*)([A-Za-z_][A-Za-z0-9_]*)\s*:/g, '$1"$2":');
  // Single-quoted strings → double-quoted (skip already-escaped content).
  s = s.replace(/'([^'\\]*(?:\\.[^'\\]*)*)'/g, (_m, inner: string) =>
    `"${inner.replace(/"/g, '\\"')}"`,
  );
  return s;
}

function tryParseJson(text: string): unknown | undefined {
  try {
    return JSON.parse(text);
  } catch {
    try {
      return JSON.parse(repairJson(text));
    } catch {
      return undefined;
    }
  }
}

function asVec2(value: unknown, arenaSize: number): Vec2 | null {
  if (typeof value === "string") {
    // "40,20" / "[40, 20]" / "40 20"
    const nums = value.match(/-?\d+(?:\.\d+)?/g);
    if (nums && nums.length >= 2) {
      const x = Number(nums[0]);
      const y = Number(nums[1]);
      if (Number.isFinite(x) && Number.isFinite(y)) {
        return [clamp(x, arenaSize), clamp(y, arenaSize)];
      }
    }
    return null;
  }
  if (Array.isArray(value) && value.length >= 2) {
    const x = Number(value[0]);
    const y = Number(value[1]);
    if (Number.isFinite(x) && Number.isFinite(y)) {
      return [clamp(x, arenaSize), clamp(y, arenaSize)];
    }
    return null;
  }
  // Some models emit {"x":1,"y":2} instead of an array.
  if (value && typeof value === "object") {
    const rec = value as Record<string, unknown>;
    const x = Number(rec.x ?? rec[0]);
    const y = Number(rec.y ?? rec[1]);
    if (Number.isFinite(x) && Number.isFinite(y)) {
      return [clamp(x, arenaSize), clamp(y, arenaSize)];
    }
  }
  return null;
}

function clamp(v: number, arenaSize: number): number {
  // Generous clamp: the engine clamps again with its own margin.
  return Math.min(arenaSize, Math.max(0, v));
}

function cleanReason(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const collapsed = value.replace(/\s+/g, " ").trim();
  if (collapsed.length === 0) return undefined;
  return collapsed.length > 120 ? `${collapsed.slice(0, 117)}...` : collapsed;
}

function resolveActionName(raw: string): ActionType | null {
  const key = raw.trim();
  const upper = key.toUpperCase();
  if (VALID_ACTIONS.has(upper)) return upper as ActionType;
  const aliased = ACTION_ALIASES[upper] ?? ACTION_ALIASES[key];
  return aliased ?? null;
}

/**
 * Parses a model response into a validated Action.
 *
 * Every failure path is explicit and returns `ok: false`; the caller decides
 * whether to fall back to a heuristic or to WAIT. We never silently coerce a
 * malformed action into a *different* intent, because that would corrupt the
 * meaning of the action log used for replay.
 */
export function parseAction(text: string, arenaSize: number): ParseOutcome {
  const json = extractJsonObject(text);
  if (!json) return { ok: false, error: "no JSON object found", raw: text };

  const parsed = tryParseJson(json);
  if (parsed === undefined) {
    return { ok: false, error: "invalid JSON", raw: text };
  }
  if (!parsed || typeof parsed !== "object") {
    return { ok: false, error: "JSON is not an object", raw: text };
  }

  const rec = parsed as Record<string, unknown>;
  const rawAction = rec.action ?? rec.type ?? rec.name;
  if (typeof rawAction !== "string") {
    return { ok: false, error: "missing action field", raw: text };
  }
  const name = resolveActionName(rawAction);
  if (!name) {
    return { ok: false, error: `unknown action "${rawAction}"`, raw: text };
  }

  const reason = cleanReason(rec.reason ?? rec.why ?? rec.note);

  switch (name) {
    case "MOVE":
    case "SCOUT": {
      const target = asVec2(rec.target ?? rec.position ?? rec.to, arenaSize);
      if (!target) {
        return { ok: false, error: `${name} missing a valid [x, y] target`, raw: text };
      }
      return {
        ok: true,
        raw: text,
        action: { action: name, target, ...(reason ? { reason } : {}) },
      };
    }
    case "COLLECT": {
      const resourceId =
        typeof rec.resourceId === "string"
          ? rec.resourceId
          : typeof rec.resource_id === "string"
            ? rec.resource_id
            : typeof rec.target === "string"
              ? rec.target
              : undefined;
      return {
        ok: true,
        raw: text,
        // No id is legal: the engine picks the best in-range resource.
        action: { action: "COLLECT", ...(resourceId ? { resourceId } : {}), ...(reason ? { reason } : {}) },
      };
    }
    case "ATTACK": {
      const targetId =
        typeof rec.targetId === "string"
          ? rec.targetId
          : typeof rec.target_id === "string"
            ? rec.target_id
            : typeof rec.target === "string"
              ? rec.target
              : "enemy";
      return {
        ok: true,
        raw: text,
        action: { action: "ATTACK", targetId, ...(reason ? { reason } : {}) },
      };
    }
    case "DEFEND": {
      const rawDuration = Number(rec.durationTicks ?? rec.duration ?? 10);
      const durationTicks = Number.isFinite(rawDuration)
        ? Math.min(30, Math.max(1, Math.round(rawDuration)))
        : 10;
      return {
        ok: true,
        raw: text,
        action: { action: "DEFEND", durationTicks, ...(reason ? { reason } : {}) },
      };
    }
    case "WAIT": {
      return {
        ok: true,
        raw: text,
        action: { action: "WAIT", ...(reason ? { reason } : {}) },
      };
    }
    case "FLEE": {
      return {
        ok: true,
        raw: text,
        action: { action: "FLEE", ...(reason ? { reason } : {}) },
      };
    }
    case "EXCHANGE":
    case "USE": {
      const item = resolveItem(rec.item ?? rec.kind ?? rec.target);
      if (!item) {
        return { ok: false, error: `${name} needs item "bomb" or "blade"`, raw: text };
      }
      return {
        ok: true,
        raw: text,
        action: { action: name, item, ...(reason ? { reason } : {}) },
      };
    }
    default:
      return { ok: false, error: "unreachable", raw: text };
  }
}

function resolveItem(value: unknown): ItemKind | null {
  if (typeof value !== "string") return null;
  const key = value.trim().toLowerCase();
  if (key === "bomb" || key === "爆炸" || key === "炸弹") return "bomb";
  if (key === "blade" || key === "刀具" || key === "旋转刀" || key === "刀") return "blade";
  return null;
}
